import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import { api, Transcript, Insight, Branch } from '../lib/api';
import { AppOutletContext } from '../App';
import { BranchChip } from '../components/BranchChip';
import { LinkInsightModal } from '../components/LinkInsightModal';

interface TranscriptWithInsights {
  transcript: Transcript;
  insights: Insight[];
  unreviewed_count: number;
}

export function Review() {
  const navigate = useNavigate();
  const outletCtx = useOutletContext<AppOutletContext>();
  const currentUser = outletCtx?.currentUser ?? null;
  const refreshBadges = outletCtx?.refreshBadges;

  const [loading, setLoading] = useState(true);
  const [localCurrentUser, setLocalCurrentUser] = useState(currentUser);
  const [transcripts, setTranscripts] = useState<TranscriptWithInsights[]>([]);
  const [expandedTranscript, setExpandedTranscript] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'unreviewed'>('unreviewed');
  const [stats, setStats] = useState({
    total_transcripts: 0,
    total_insights: 0,
    unreviewed: 0,
    valid: 0,
    invalid: 0,
  });

  // "Validate & link now" modal state
  const [linkModalInsight, setLinkModalInsight] = useState<Insight | null>(null);

  useEffect(() => {
    // If outlet context doesn't have the user yet, self-fetch as fallback.
    // fetchAPI already redirects to /login on a 401, so there's nothing
    // else to do here on failure.
    if (!currentUser) {
      api.getCurrentUser()
        .then(u => setLocalCurrentUser(u))
        .catch(() => {});
    } else {
      setLocalCurrentUser(currentUser);
    }
  }, [currentUser]);

  useEffect(() => {
    if (localCurrentUser) {
      loadReviewQueue();
    }
  }, [localCurrentUser]);

  const loadReviewQueue = async () => {
    try {
      setLoading(true);
      // Get recent transcripts (last 30 that are ready)
      const transcriptList = await api.listTranscripts({ status: 'ready', limit: 30 });

      // Filter transcripts based on role:
      // - Founders and Admins see all transcripts
      // - Uploaders see only their own transcripts + legacy (uploaded_by = null)
      const hasElevatedAccess = localCurrentUser?.role === 'founder' || localCurrentUser?.role === 'admin';
      const filteredTranscripts = hasElevatedAccess
        ? transcriptList
        : transcriptList.filter(t =>
            t.uploaded_by === localCurrentUser?.user_id || t.uploaded_by === null
          );

      // For each transcript, get its insights
      const transcriptsWithInsights: TranscriptWithInsights[] = [];

      for (const transcript of filteredTranscripts) {
        const insights = await api.getInsightsByTranscript(transcript.transcript_id);
        const unreviewed_count = insights.filter(i => i.feedback_status === 'unreviewed').length;

        // Only include transcripts that have insights
        if (insights.length > 0) {
          transcriptsWithInsights.push({
            transcript,
            insights,
            unreviewed_count,
          });
        }
      }

      // Sort by most unreviewed first, then by date
      transcriptsWithInsights.sort((a, b) => {
        if (a.unreviewed_count !== b.unreviewed_count) {
          return b.unreviewed_count - a.unreviewed_count;
        }
        return new Date(b.transcript.meeting_date).getTime() - new Date(a.transcript.meeting_date).getTime();
      });

      setTranscripts(transcriptsWithInsights);

      // Calculate stats
      const total_insights = transcriptsWithInsights.reduce((sum, t) => sum + t.insights.length, 0);
      const unreviewed = transcriptsWithInsights.reduce((sum, t) =>
        sum + t.insights.filter(i => i.feedback_status === 'unreviewed').length, 0);
      const valid = transcriptsWithInsights.reduce((sum, t) =>
        sum + t.insights.filter(i => i.feedback_status === 'valid').length, 0);
      const invalid = transcriptsWithInsights.reduce((sum, t) =>
        sum + t.insights.filter(i => i.feedback_status === 'invalid').length, 0);

      setStats({
        total_transcripts: transcriptsWithInsights.length,
        total_insights,
        unreviewed,
        valid,
        invalid,
      });
    } catch (err) {
      setError('Failed to load review queue');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (insightId: string, status: 'valid' | 'invalid') => {
    try {
      await api.updateFeedback(insightId, status);
      // Update local state
      setTranscripts(prev => prev.map(t => ({
        ...t,
        insights: t.insights.map(i =>
          i.insight_id === insightId ? { ...i, feedback_status: status } : i
        ),
        unreviewed_count: t.insights.filter(i =>
          i.insight_id === insightId ? false : i.feedback_status === 'unreviewed'
        ).length,
      })));
      // Update stats
      setStats(prev => ({
        ...prev,
        unreviewed: prev.unreviewed - 1,
        [status]: prev[status] + 1,
      }));
      refreshBadges?.();
    } catch (err) {
      setError('Failed to update feedback');
    }
  };

  const handleMarkAllValid = async (transcriptId: string) => {
    const transcript = transcripts.find(t => t.transcript.transcript_id === transcriptId);
    if (!transcript) return;

    const unreviewed = transcript.insights.filter(i => i.feedback_status === 'unreviewed');

    try {
      await Promise.all(
        unreviewed.map(insight => api.updateFeedback(insight.insight_id, 'valid'))
      );
      // Update local state
      setTranscripts(prev => prev.map(t =>
        t.transcript.transcript_id === transcriptId
          ? {
              ...t,
              insights: t.insights.map(i =>
                i.feedback_status === 'unreviewed' ? { ...i, feedback_status: 'valid' } : i
              ),
              unreviewed_count: 0,
            }
          : t
      ));
      // Update stats
      setStats(prev => ({
        ...prev,
        unreviewed: prev.unreviewed - unreviewed.length,
        valid: prev.valid + unreviewed.length,
      }));
      refreshBadges?.();
    } catch (err) {
      setError('Failed to mark all as valid');
    }
  };

  const handleBranchChange = async (insightId: string, branch: Branch | null) => {
    try {
      await api.updateInsightBranch(insightId, branch);
      setTranscripts(prev => prev.map(t => ({
        ...t,
        insights: t.insights.map(i =>
          i.insight_id === insightId ? { ...i, branch } : i
        ),
      })));
    } catch {
      // silent — branch chip will visually revert on next render
    }
  };

  // "Validate & link now": first mark valid, then open link modal
  const handleValidateAndLink = async (insight: Insight) => {
    if (insight.feedback_status === 'unreviewed') {
      try {
        await api.updateFeedback(insight.insight_id, 'valid');
        // Reflect valid status locally so re-render is immediate
        setTranscripts(prev => prev.map(t => ({
          ...t,
          insights: t.insights.map(i =>
            i.insight_id === insight.insight_id ? { ...i, feedback_status: 'valid' } : i
          ),
          unreviewed_count: t.insights.filter(i =>
            i.insight_id === insight.insight_id ? false : i.feedback_status === 'unreviewed'
          ).length,
        })));
        setStats(prev => ({
          ...prev,
          unreviewed: prev.unreviewed - 1,
          valid: prev.valid + 1,
        }));
        refreshBadges?.();
        // Open link modal with the now-valid insight (update local ref)
        setLinkModalInsight({ ...insight, feedback_status: 'valid' });
      } catch {
        setError('Failed to mark as valid');
      }
    } else {
      // Already valid — just open the link modal
      setLinkModalInsight(insight);
    }
  };

  const handleLinked = (_roadmapItemId: string, _roadmapItemTitle: string) => {
    if (!linkModalInsight) return;
    // Remove from local pending list (disposition becomes linked)
    setTranscripts(prev => prev.map(t => ({
      ...t,
      insights: t.insights.filter(i => i.insight_id !== linkModalInsight.insight_id),
    })).filter(t => t.insights.length > 0));
    setLinkModalInsight(null);
    refreshBadges?.();
  };

  const toggleExpanded = (transcriptId: string) => {
    setExpandedTranscript(prev => prev === transcriptId ? null : transcriptId);
  };

  const filteredTranscripts = filter === 'unreviewed'
    ? transcripts.filter(t => t.unreviewed_count > 0)
    : transcripts;

  const getUrgencyClass = (urgency: string) => {
    const base = 'tag';
    switch (urgency) {
      case 'blocker': return `${base} tag-error`;
      case 'strong_want': return `${base} tag-warning`;
      default: return base;
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading review queue...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Review Extractions</h1>
        <p className="page-subtitle">
          {(localCurrentUser?.role === 'founder' || localCurrentUser?.role === 'admin')
            ? 'Validate AI-extracted insights from all meeting transcripts'
            : 'Validate AI-extracted insights from your meeting transcripts'}
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Stats */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.total_transcripts}</div>
          <div className="stat-label">Meetings</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.total_insights}</div>
          <div className="stat-label">Insights</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.unreviewed}</div>
          <div className="stat-label">To Review</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.valid}</div>
          <div className="stat-label">Valid</div>
        </div>
      </div>

      {/* Explanation */}
      <div className="card mb-2">
        <div className="card-content">
          <p className="text-small">
            <strong>Review extracted insights.</strong> You were in these meetings, so you know what actually happened.
            Mark insights as valid or invalid to improve extraction quality and prepare items for roadmap consideration.
          </p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="filters-bar mb-2">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'unreviewed' ? 'active' : ''}`}
            onClick={() => setFilter('unreviewed')}
          >
            Needs Review ({transcripts.filter(t => t.unreviewed_count > 0).length})
          </button>
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Meetings ({transcripts.length})
          </button>
        </div>
      </div>

      {/* Transcript List */}
      {filteredTranscripts.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <p className="text-muted">
              {filter === 'unreviewed' ? 'All caught up!' : 'No transcripts with insights'}
            </p>
            <p className="text-small text-muted">
              {filter === 'unreviewed'
                ? 'You have reviewed all extracted insights. Submit more transcripts to continue.'
                : 'Submit transcripts to see extracted insights here.'}
            </p>
            <button
              className="btn btn-secondary mt-1"
              onClick={() => navigate('/capture/transcript')}
            >
              Submit Transcript
            </button>
          </div>
        </div>
      ) : (
        <div className="review-queue">
          {filteredTranscripts.map((item) => (
            <div key={item.transcript.transcript_id} className="card review-card">
              {/* Transcript Header */}
              <div
                className="review-header clickable"
                onClick={() => toggleExpanded(item.transcript.transcript_id)}
              >
                <div className="review-header-left">
                  <span className="review-client">{item.transcript.client_name}</span>
                  <span className="review-date">{item.transcript.meeting_date}</span>
                  <span className="tag">{item.transcript.meeting_type}</span>
                </div>
                <div className="review-header-right">
                  {item.unreviewed_count > 0 && (
                    <span className="badge badge-warning">{item.unreviewed_count} to review</span>
                  )}
                  <span className="text-small text-muted">
                    {item.insights.length} insight{item.insights.length !== 1 ? 's' : ''}
                  </span>
                  <span className="expand-icon">
                    {expandedTranscript === item.transcript.transcript_id ? '−' : '+'}
                  </span>
                </div>
              </div>

              {/* Expanded Insights */}
              {expandedTranscript === item.transcript.transcript_id && (
                <div className="review-insights">
                  {/* Quick Actions */}
                  {item.unreviewed_count > 0 && (
                    <div className="review-actions-bar">
                      <button
                        className="btn btn-secondary btn-small"
                        onClick={() => handleMarkAllValid(item.transcript.transcript_id)}
                      >
                        Mark All Valid ({item.unreviewed_count})
                      </button>
                      <span className="text-small text-muted">
                        or review individually below
                      </span>
                    </div>
                  )}

                  {/* Insight List */}
                  <div className="insight-review-list">
                    {item.insights.map((insight) => (
                      <div
                        key={insight.insight_id}
                        className={`insight-review-item ${insight.feedback_status !== 'unreviewed' ? 'reviewed' : ''}`}
                      >
                        <div className="insight-review-content">
                          <div className="insight-review-text">{insight.insight_text}</div>
                          <div className="insight-review-quote">{insight.supporting_quote}</div>
                          <div className="insight-review-meta">
                            <span className={getUrgencyClass(insight.urgency)}>
                              {insight.urgency.replace('_', ' ')}
                            </span>
                            <span className="tag">{insight.module_touched.replace('_', ' ')}</span>
                            {insight.capability_tag && (
                              <span className="text-small text-muted">#{insight.capability_tag}</span>
                            )}
                            {/* Editable branch chip */}
                            <BranchChip
                              branch={insight.branch}
                              editable
                              onChange={branch => handleBranchChange(insight.insight_id, branch)}
                            />
                          </div>
                        </div>
                        <div className="insight-review-actions">
                          {insight.feedback_status === 'unreviewed' ? (
                            <>
                              <button
                                className="btn btn-success btn-small"
                                onClick={() => handleFeedback(insight.insight_id, 'valid')}
                              >
                                Valid
                              </button>
                              <button
                                className="btn btn-gradient btn-small"
                                onClick={() => handleValidateAndLink(insight)}
                                title="Mark valid and link to roadmap now"
                              >
                                Validate &amp; link
                              </button>
                              <button
                                className="btn btn-ghost btn-small"
                                onClick={() => handleFeedback(insight.insight_id, 'invalid')}
                              >
                                Invalid
                              </button>
                            </>
                          ) : insight.feedback_status === 'valid' ? (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                              <span className="tag tag-success">valid</span>
                              <button
                                className="btn btn-secondary btn-small"
                                onClick={() => handleValidateAndLink(insight)}
                                title="Link to roadmap item"
                              >
                                Link to roadmap
                              </button>
                            </div>
                          ) : (
                            <span className={`tag ${insight.feedback_status === 'valid' ? 'tag-success' : 'tag-error'}`}>
                              {insight.feedback_status}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Validate & link modal */}
      {linkModalInsight && (
        <LinkInsightModal
          insight={linkModalInsight}
          currentUserId={localCurrentUser?.user_id}
          onLinked={handleLinked}
          onClose={() => setLinkModalInsight(null)}
        />
      )}
    </div>
  );
}
